import type { NextFn } from '@warp-drive/core/request';
import type { RequestContext } from '@warp-drive/core/types/request';
import { SkipErrorReporting, SkipErrorToast } from '../request-options.ts';

/** Read at request time, like the impersonation gate, so one handler instance follows the session. */
type IsReadOnly = () => boolean;

/** What the handler refused, for the app to surface (or not). */
export interface BlockedWrite {
  method: string;
  url: string;
  /** The caller opted out of error surfacing, so the refusal should stay quiet. */
  silent: boolean;
}

export interface ReadOnlyHandlerOptions {
  isReadOnly: IsReadOnly;
  onBlocked?: (blocked: BlockedWrite) => void;
  /** The API origin the search exception belongs to; unset means the API is served relative to the page. */
  apiHost?: string;
}

const MUTATING_METHODS = new Set(['POST', 'PATCH', 'PUT', 'DELETE']);

/** `POST /api/v2/search` is the one write the API reads as a read (its only `read_safe_actions` tenant); its siblings stay writes. */
const READ_SAFE_PATH = '/api/v2/search';

const FALLBACK_BASE = 'http://localhost';

/** The API's origin as a URL base; a relative `apiHost` (or none) means the page's own origin stands in. */
function apiOrigin(apiHost?: string) {
  try {
    return new URL(apiHost || FALLBACK_BASE, FALLBACK_BASE).origin;
  } catch {
    return FALLBACK_BASE;
  }
}

/** Thrown in place of the request, so a caller can tell a refused write from a network failure. */
export class TioReadOnlyError extends Error {
  readonly isReadOnly = true;
  readonly method: string;
  readonly url: string;

  constructor(method: string, url: string) {
    super(`Request refused: ${method} ${url} is not allowed in a read-only session`);
    this.name = 'TioReadOnlyError';
    this.method = method;
    this.url = url;
  }
}

/** @returns whether `error` is the handler's refusal rather than a transport or API error */
export function isReadOnlyError(error: unknown): error is TioReadOnlyError {
  return error instanceof TioReadOnlyError;
}

/**
 * @param method upper-cased HTTP verb
 * @param url the request URL, absolute or relative to the API; dot segments are resolved first
 * @param apiHost the API origin the search exception belongs to; a search on any other origin is a write
 * @returns whether the API would accept this request from a read-only principal
 */
export function isReadSafeRequest(method: string, url: string, apiHost?: string): boolean {
  if (!MUTATING_METHODS.has(method)) {
    return true;
  }
  if (method !== 'POST') {
    return false;
  }
  const origin = apiOrigin(apiHost);
  let target: URL;
  try {
    target = new URL(url, origin);
  } catch {
    return false;
  }
  return target.origin === origin && target.pathname.replace(/\/+$/, '') === READ_SAFE_PATH;
}

/** Refuses every write of a read-only session before it leaves the browser, mirroring the API's own verb check. */
export class TioReadOnlyHandler {
  constructor(private options: ReadOnlyHandlerOptions) {}

  /**
   * @throws TioReadOnlyError for a write while the session is read-only; the
   *   request is never sent
   */
  async request<T>(context: RequestContext, next: NextFn<T>) {
    const { request } = context;
    const method = (request.method ?? 'GET').toUpperCase();
    const url = request.url ?? '';

    if (this.options.isReadOnly() && !isReadSafeRequest(method, url, this.options.apiHost)) {
      const options = request.options as Record<string, unknown> | undefined;
      this.options.onBlocked?.({
        method,
        url,
        silent: options?.[SkipErrorReporting] === true || options?.[SkipErrorToast] === true,
      });
      throw new TioReadOnlyError(method, url);
    }

    return next(request);
  }
}
